import { Facebook, Instagram, Twitter, Youtube, ArrowUp } from "lucide-react";
import * as LucideIcons from "lucide-react";

interface SocialLink {
  icon?: any;
  icon_name?: string;
  href?: string;
  url?: string;
  label?: string;
  platform?: string;
}

interface FooterProps {
  socialLinks?: SocialLink[];
}

const defaultSocialLinks: SocialLink[] = [
  { icon: Facebook, href: "https://facebook.com/baajmedia", label: "Facebook" },
  { icon: Instagram, href: "https://instagram.com/baajmedia", label: "Instagram" },
  { icon: Twitter, href: "https://twitter.com/baajmedia", label: "Twitter" },
  { icon: Youtube, href: "https://youtube.com/baajmedia", label: "YouTube" },
];

const navLinks = [
  { name: "Account", href: "/admin_login" },
  { name: "Projects", href: "#projects" },
  { name: "Services", href: "#services" },
  { name: "About", href: "#about" },
  { name: "Contact", href: "#contact" },
];

export function Footer({ socialLinks }: FooterProps) {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const displaySocialLinks = socialLinks && socialLinks.length > 0 ? socialLinks : defaultSocialLinks;

  return (
    <footer className="bg-card border-t border-border/50">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <a href="#home" className="inline-block mb-6">
              <span className="font-display text-4xl text-gradient-gold tracking-wider">
                BAAJ MEDIA
              </span>
            </a>
            <p className="text-muted-foreground leading-relaxed max-w-md mb-6">
              India's leading digital marketing company since 2017. We help
              brands grow their digital presence through innovative strategies
              and creative excellence.
            </p>
            <div className="flex gap-4">
              {displaySocialLinks.map((social) => {
                const IconComponent = social.icon_name 
                  // @ts-ignore
                  ? LucideIcons[social.icon_name] || LucideIcons.Link
                  : (social.icon || LucideIcons.Link);

                return (
                  <a
                    key={social.label || social.platform}
                    href={social.href || social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-secondary hover:bg-gradient-gold flex items-center justify-center transition-all duration-300 group"
                    aria-label={social.label || social.platform}
                  >
                    <IconComponent
                      size={18}
                      className="text-foreground group-hover:text-primary-foreground transition-colors"
                    />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-xl text-foreground mb-6">
              QUICK LINKS
            </h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-display text-xl text-foreground mb-6">
              CONTACT
            </h4>
            <ul className="space-y-3 text-muted-foreground">
              <li>
                <a
                  href="mailto:baajmediaofficial@gmail.com"
                  className="hover:text-primary transition-colors"
                >
                  baajmediaofficial@gmail.com
                </a>
              </li>
              <li>
                <a
                  href="tel:+919729090280"
                  className="hover:text-primary transition-colors"
                >
                  +91 9729090280
                </a>
              </li>
              <li>India, UK</li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-border/50 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} Baaj Media. All rights reserved.
          </p>
          <button
            onClick={scrollToTop}
            className="w-10 h-10 rounded-full bg-secondary hover:bg-gradient-gold flex items-center justify-center transition-all duration-300 group"
            aria-label="Scroll to top"
          >
            <ArrowUp
              size={18}
              className="text-foreground group-hover:text-primary-foreground transition-colors"
            />
          </button>
        </div>
      </div>
    </footer>
  );
}
